import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Container from '../../components/shared/Container';
import Button from '../../components/shared/Button';
import { Link } from 'react-router-dom';
import { useHeader } from '../../context/HeaderContext';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { generateBreadcrumbSchema } from '../../utils/schema';
import { NY_GIFT_CARDS_COMING_SOON } from '../../config/featureFlags';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

export default function GiftCardsPage() {
  const { setUseBlurredBackground } = useHeader();

  // Enable blurred background for header on gift cards page
  useEffect(() => {
    setUseBlurredBackground(true);
    
    // Clean up when component unmounts
    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);

  // Define the URLs for different regions
  const giftCardUrls = {
    washington: {
      purchase: 'https://originalpancakehousewa.shop.securetree.com/',
      checkBalance: 'https://lookup.app.securetree.com/lookup/originalpancakehousebothe'
    },
    // Legacy shared WA/NY portal, kept until New York gets its own storefront.
    newYork: {
      purchase: 'https://iloveoph.myguestaccount.com/en-us/guest/egift?page=cardInfo',
      checkBalance: 'https://iloveoph.myguestaccount.com/en-us/guest/nologin/account-balance'
    },
    indiana: {
      purchase: 'https://originalpancakehouseindiana.shop.securetree.com/',
      checkBalance: 'https://lookup.app.securetree.com/lookup/originalpancakehousefishe'
    }
  };

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title="Gift Cards | The Original Pancake House - Give the Gift of Breakfast" 
        description="Give the gift of delicious breakfast with an Original Pancake House gift card. Perfect for birthdays, holidays, or any special occasion. Purchase online or check your balance today. Valid at participating locations."
        canonicalUrl="/gift-cards"
        openGraph={{
          image: "/oph-social-share4.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Gift Cards', url: '/gift-cards' }
      ])} />
      
      {/* Hero Section */}
      <motion.div 
        className="relative h-[400px] bg-brand-navy"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `url(/images/hero/blueberry-pancakes-original-pancake-house.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Gradient Overlay - Darker on mobile for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30 lg:via-transparent lg:to-transparent"></div>
        
        <Container className="relative h-full">
          <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end">
              {/* Left side - Title and breadcrumb */}
              <div className="mb-4 md:mb-0">
                <div className="mb-4">
                  <div className="flex items-center text-sm font-medium">
                    <Link 
                      to="/"
                      className="text-white hover:text-brand-cream transition-colors"
                    >
                      Home
                    </Link>
                    
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    
                    <span className="text-white text-opacity-90">
                      Gift Cards
                    </span>
                  </div>
                </div>
                <h1 className="text-5xl md:text-6xl font-heading text-white drop-shadow-lg">Gift Cards</h1>
              </div>
              
              {/* Right side - Description */}
              <div className="md:max-w-xl">
                <p className="text-white text-opacity-90 drop-shadow-md">
                  Share the joy of a delicious breakfast with friends and family. Original Pancake House gift cards are perfect for any occasion and can be used at participating locations.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </motion.div>

      {/* Why Gift Cards Section */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              THE PERFECT GIFT FOR ANY OCCASION
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {/* Reason 1 */}
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-24 h-24 rounded-full border-2 border-brand-navy flex items-center justify-center bg-white mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2">BIRTHDAYS & CELEBRATIONS</h3>
                <p className="text-gray-700">
                  Celebrate special moments with the gift of a memorable breakfast experience
                </p>
              </motion.div>

              {/* Reason 2 */}
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-24 h-24 rounded-full border-2 border-brand-navy flex items-center justify-center bg-white mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2">SHOW YOUR APPRECIATION</h3>
                <p className="text-gray-700">
                  Thank someone special with a thoughtful gift they'll truly enjoy
                </p>
              </motion.div>

              {/* Reason 3 */}
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-24 h-24 rounded-full border-2 border-brand-navy flex items-center justify-center bg-white mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-brand-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2">QUICK & CONVENIENT</h3>
                <p className="text-gray-700">
                  Purchase instantly online and send via email for last-minute gifting
                </p>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Purchase Gift Cards Section */}
      <section className="py-16 bg-brand-cream">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              PURCHASE A GIFT CARD
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Washington Card */}
              <motion.div 
                variants={itemVariants}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className="bg-brand-navy text-white p-6">
                  <h3 className="text-2xl font-heading mb-2">WASHINGTON</h3>
                  <p>For locations in Washington</p>
                </div>
                <div className="p-6">
                  <img 
                    src="/images/hero/french-toast-original-pancake-house-hero.jpg" 
                    alt="Washington Gift Card" 
                    className="w-full h-48 object-cover rounded-md mb-6"
                    loading="lazy"
                  />
                  <div className="space-y-3">
                    <Button 
                      as="a"
                      href={giftCardUrls.washington.purchase}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="solid" 
                      color="navy"
                      className="w-full"
                      icon={
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      }
                    >
                      Purchase Gift Card
                    </Button>
                    <Button 
                      as="a"
                      href={giftCardUrls.washington.checkBalance}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline" 
                      color="navy"
                      className="w-full"
                      icon={
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      }
                    >
                      Check Balance
                    </Button>
                  </div>
                </div>
              </motion.div>

              {/* New York Card */}
              <motion.div 
                variants={itemVariants}
                className="relative bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div
                  className={NY_GIFT_CARDS_COMING_SOON ? 'pointer-events-none select-none' : ''}
                  aria-hidden={NY_GIFT_CARDS_COMING_SOON || undefined}
                >
                  <div className="bg-brand-navy text-white p-6">
                    <h3 className="text-2xl font-heading mb-2">NEW YORK</h3>
                    <p>For locations in New York</p>
                  </div>
                  <div className="p-6">
                    <img 
                      src="/images/hero/country-benedict-oph.jpg" 
                      alt="New York Gift Card" 
                      className="w-full h-48 object-cover rounded-md mb-6"
                      loading="lazy"
                    />
                    <div className="space-y-3">
                      <Button 
                        as="a"
                        href={giftCardUrls.newYork.purchase}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="solid" 
                        color="navy"
                        className="w-full"
                        disabled={NY_GIFT_CARDS_COMING_SOON}
                        icon={
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                        }
                      >
                        Purchase Gift Card
                      </Button>
                      <Button 
                        as="a"
                        href={giftCardUrls.newYork.checkBalance}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outline" 
                        color="navy"
                        className="w-full"
                        disabled={NY_GIFT_CARDS_COMING_SOON}
                        icon={
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        }
                      >
                        Check Balance
                      </Button>
                    </div>
                  </div>
                </div>

                {NY_GIFT_CARDS_COMING_SOON && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[2px] p-6">
                    <div className="bg-brand-navy text-white rounded-lg shadow-lg px-8 py-6 text-center">
                      <p className="text-3xl font-heading mb-2">COMING SOON</p>
                      <p className="text-sm text-white text-opacity-90">
                        New York gift cards are temporarily unavailable.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Indiana Card */}
              <motion.div 
                variants={itemVariants}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className="bg-brand-navy text-white p-6">
                  <h3 className="text-2xl font-heading mb-2">INDIANA</h3>
                  <p>For locations in Indiana</p>
                </div>
                <div className="p-6">
                  <img 
                    src="/images/hero/california-burrito-original-pancake-house.jpg" 
                    alt="Indiana Gift Card" 
                    className="w-full h-48 object-cover rounded-md mb-6"
                    loading="lazy"
                  />
                  <div className="space-y-3">
                    <Button 
                      as="a"
                      href={giftCardUrls.indiana.purchase}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="solid" 
                      color="navy"
                      className="w-full"
                      icon={
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      }
                    >
                      Purchase Gift Card
                    </Button>
                    <Button 
                      as="a"
                      href={giftCardUrls.indiana.checkBalance}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline" 
                      color="navy"
                      className="w-full"
                      icon={
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      }
                    >
                      Check Balance
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              FREQUENTLY ASKED QUESTIONS
            </motion.h2>

            <div className="space-y-6">
              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold text-brand-navy mb-2">Where can I use my gift card?</h3>
                <p className="text-gray-700">
                  Gift cards can be used at participating Original Pancake House locations in the region where they were purchased. Washington, New York, and Indiana each have their own gift card program, so a card purchased for one region cannot be redeemed in another.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold text-brand-navy mb-2">Do gift cards expire?</h3>
                <p className="text-gray-700">
                  No, our gift cards do not expire. Your balance will remain available until fully used.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold text-brand-navy mb-2">Can I reload my gift card?</h3>
                <p className="text-gray-700">
                  Yes, you can add additional funds to your gift card at any time through the purchase portal for your region.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold text-brand-navy mb-2">What if my gift card is lost or stolen?</h3>
                <p className="text-gray-700">
                  If you have your gift card number, you can check the balance online. For assistance with lost or stolen cards, please contact the location where the card was purchased.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold text-brand-navy mb-2">Can I use a gift card for online ordering?</h3>
                <p className="text-gray-700">
                  Gift card acceptance for online ordering may vary by location. Please check with your local Original Pancake House for their online ordering gift card policy.
                </p>
              </motion.div>
            </div>

            <motion.div variants={itemVariants} className="mt-12 text-center">
              <p className="text-gray-700 mb-4">
                Have more questions about our gift cards?
              </p>
              <Button 
                href="/contact"
                variant="outline" 
                color="navy"
              >
                Contact Us
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}

